import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { ChevronDown, CheckCircle2, Circle, XCircle, MapPinOff, Clock, Calendar, Phone } from 'lucide-react';

const STATUS_CONFIG = {
  delivered: { label: 'Delivered', color: '#4ADE80', bg: 'rgba(74,222,128,0.1)', border: 'rgba(74,222,128,0.2)', icon: CheckCircle2 },
  skipped: { label: 'Skipped', color: '#FBBF24', bg: 'rgba(251,191,36,0.1)', border: 'rgba(251,191,36,0.2)', icon: Clock },
  'out-of-town': { label: 'Out of Town', color: '#A855F7', bg: 'rgba(168,85,247,0.1)', border: 'rgba(168,85,247,0.2)', icon: MapPinOff },
  missed: { label: 'Missed', color: '#F87171', bg: 'rgba(248,113,113,0.1)', border: 'rgba(248,113,113,0.2)', icon: XCircle },
  pending: { label: 'Pending', color: '#C8A96E', bg: 'rgba(200,169,110,0.1)', border: 'rgba(200,169,110,0.2)', icon: Circle },
};

export default function DeliveryHistoryPage() {
  const [subs, setSubs] = useState([]);
  const [selectedSub, setSelectedSub] = useState('');
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [marking, setMarking] = useState('');

  useEffect(() => {
    api.getSubscribers({}).then(res => setSubs(res.data)).catch(err => console.error('Fetch error:', err.message));
  }, []);

  const fetchHistory = useCallback(async (subId) => {
    if (!subId) { setHistory(null); return; }
    setLoading(true);
    try {
      const res = await api.getDeliveryHistory(subId);
      setHistory(res.data);
    } catch (err) { console.error('History error:', err.message); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchHistory(selectedSub); }, [selectedSub, fetchHistory]);

  const handleMarkDelivered = async (date) => {
    if (!selectedSub) return;
    setMarking(date);
    try {
      await api.markDelivered({ subscriberId: selectedSub, date });
      await fetchHistory(selectedSub);
    } catch (err) { console.error('Mark error:', err.message); }
    setMarking('');
  };

  return (
    <div data-testid="delivery-history-panel" className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>Delivery History</h2>
        <p className="text-sm mt-1" style={{ color: '#A8A29E' }}>Date-wise delivery tracking per subscriber. Click any date to mark/unmark delivery.</p>
      </div>

      {/* Subscriber selector */}
      <div className="relative max-w-md">
        <select data-testid="history-subscriber" value={selectedSub} onChange={e => setSelectedSub(e.target.value)}
          className="w-full px-4 py-3 rounded-lg text-sm appearance-none" style={{ background: '#1E1A17', border: '1px solid #2A241F', color: '#F5F5F4' }}>
          <option value="">-- Select a subscriber --</option>
          {subs.map(s => <option key={s.id} value={s.id}>{s.name} ({s.apt} / {s.flat})</option>)}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: '#78716C' }} />
      </div>

      {loading ? (
        <div className="text-center py-12" style={{ color: '#78716C' }}>Loading history...</div>
      ) : !history ? (
        <div className="text-center py-12 rounded-xl" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)', color: '#78716C' }}>
          <Calendar className="w-12 h-12 mx-auto mb-3" style={{ color: '#2A241F' }} />
          <p>Select a subscriber to view delivery history</p>
        </div>
      ) : (
        <>
          {/* Subscriber info */}
          <div className="rounded-xl p-4 flex items-center gap-4" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)' }}>
            <div className="flex-1">
              <h3 className="font-semibold" style={{ color: '#F5F5F4' }}>{history.subscriber.name}</h3>
              <p className="text-sm" style={{ color: '#A8A29E' }}>{history.subscriber.apt} / {history.subscriber.flat} &middot; Started {history.subscriber.start}</p>
            </div>
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {Object.entries(history.summary).filter(([k]) => k !== 'total').map(([key, val]) => {
              const cfg = STATUS_CONFIG[key] || STATUS_CONFIG.pending;
              return (
                <div key={key} className="rounded-xl p-4" style={{ background: '#1E1A17', border: `1px solid ${cfg.border}` }}>
                  <div className="text-2xl font-bold" style={{ fontFamily: 'Outfit', color: cfg.color }}>{val}</div>
                  <div className="text-xs mt-1" style={{ color: '#A8A29E' }}>{cfg.label}</div>
                </div>
              );
            })}
          </div>

          {/* Date-wise history */}
          <div className="rounded-xl overflow-hidden" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)' }}>
            <div className="p-4" style={{ borderBottom: '1px solid #2A241F' }}>
              <h3 className="text-sm font-semibold" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>
                Day-by-Day Log ({history.history.length} delivery days)
              </h3>
            </div>
            <div className="divide-y max-h-[60vh] overflow-y-auto" style={{ borderColor: '#2A241F' }}>
              {history.history.length === 0 ? (
                <div className="p-8 text-center" style={{ color: '#78716C' }}>No delivery days yet</div>
              ) : [...history.history].reverse().map(h => {
                const cfg = STATUS_CONFIG[h.status] || STATUS_CONFIG.pending;
                const Icon = cfg.icon;
                const isToday = h.date === new Date().toISOString().split('T')[0];
                const canToggle = h.status === 'delivered' || h.status === 'missed' || h.status === 'pending';

                return (
                  <div key={h.date} data-testid={`history-${h.date}`}
                    className="px-4 py-3 flex items-center gap-3 transition-colors"
                    style={{ background: isToday ? 'rgba(200,169,110,0.05)' : 'transparent' }}>
                    {/* Toggle delivery */}
                    <button
                      data-testid={`toggle-${h.date}`}
                      onClick={() => canToggle && handleMarkDelivered(h.date)}
                      disabled={!canToggle || marking === h.date}
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all"
                      style={{
                        background: cfg.bg, border: `1px solid ${cfg.border}`,
                        cursor: canToggle ? 'pointer' : 'default', opacity: marking === h.date ? 0.5 : 1
                      }}>
                      <Icon className="w-4 h-4" style={{ color: cfg.color }} />
                    </button>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium" style={{ color: '#F5F5F4' }}>{h.date}</span>
                        <span className="text-xs" style={{ color: '#78716C' }}>{h.day}</span>
                        {isToday && <span className="px-1.5 py-0.5 rounded text-xs font-semibold" style={{ background: 'rgba(200,169,110,0.15)', color: '#C8A96E' }}>Today</span>}
                      </div>
                    </div>

                    <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold flex-shrink-0"
                      style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}>
                      {cfg.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
